import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Calculate,
  Close,
  DeleteOutline,
  Edit,
  EventNote,
  AddPhotoAlternate,
  Inventory2,
  PictureAsPdf,
  PhotoCamera,
  Refresh,
  RemoveCircleOutline,
  Save,
  SendOutlined,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import IUser from '../../main/interfaces/IUser';
import useGetUser from '../../main/hooks/useGetUser';
import {
  IDailyInventoryClose,
  IDailyInventoryCloseListItem,
  IDailyInventoryCloseListResponse,
  IDailyInventoryCloseUser,
} from '../../main/interfaces/IDailyInventoryClose';
import { compressImagesIfNeeded } from '../../main/utils/imageCompression';
import { getMetadataFileUrl } from '../../main/utils/entityGallery';
import { useAppTheme } from '../../main/theme/AppThemeContext';
import './DailyInventoryClose.css';

const MAX_ATTACHMENT_KB = 500;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

type PendingAttachment = {
  file: File;
  previewUrl: string;
};

interface DailyInventoryCloseProps {
  open?: boolean;
  reportId?: number | null;
  onClose?: () => void;
  onSaved?: () => void;
}

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeDateValue = (value?: string | Date | null) => {
  if (!value) return formatDateForInput(new Date());
  if (value instanceof Date) return formatDateForInput(value);
  return String(value).slice(0, 10);
};

const normalizeUserSnapshot = (user: IDailyInventoryCloseUser): IDailyInventoryCloseUser => ({
  ...user,
  userId: Number(user.userId ?? 0),
  userDisplayName: user.userDisplayName ?? '',
  totalRevenue: Number(user.totalRevenue ?? 0),
  products: (user.products ?? []).map((product) => ({
    ...product,
    productId: product.productId ?? null,
    productName: product.productName ?? '',
    quantity: Number(product.quantity ?? 0),
    revenue: Number(product.revenue ?? 0),
    sourceType: product.sourceType ?? '',
  })),
});

const displayUserName = (user?: IUser) => {
  if (!user) return '';
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName || user.userName || `User #${user.id}`;
};

const formatNumber = (value: number, locale: string) =>
  new Intl.NumberFormat(locale || 'sq-AL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const stripHtml = (value: string) => {
  const div = document.createElement('div');
  div.innerHTML = value || '';
  return (div.textContent || div.innerText || '').trim();
};

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const DailyInventoryClose: React.FC<DailyInventoryCloseProps> = ({ open = true, reportId: reportIdProp = null, onClose, onSaved }) => {
  const { i18n } = useTranslation();
  const { theme } = useAppTheme();
  const currentUser = useGetUser();
  const currencySymbol = currentUser?.companyCurrencySymbol ?? '';
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const [reportId, setReportId] = useState(0);
  const [reportDate, setReportDate] = useState(formatDateForInput(new Date()));
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<IDailyInventoryCloseUser[]>([]);
  const [notes, setNotes] = useState('');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [attachments, setAttachments] = useState<IDailyInventoryClose['attachments']>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingAttachment[]>([]);
  const [recentReports, setRecentReports] = useState<IDailyInventoryCloseListItem[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingReportId, setLoadingReportId] = useState<number | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendUserId, setSendUserId] = useState(0);
  const [sendingReport, setSendingReport] = useState(false);

  const formatCurrency = useCallback(
    (value: number) => {
      const formatted = new Intl.NumberFormat(i18n.language || 'sq-AL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
      return currencySymbol ? `${formatted} ${currencySymbol}` : formatted;
    },
    [currencySymbol, i18n.language],
  );

  const selectedUser = useMemo(() => users.find((user) => user.id === selectedUserId), [selectedUserId, users]);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  const hydrateReport = useCallback(
    (report: IDailyInventoryClose, preservePendingFiles = false) => {
      setReportId(Number(report.id || 0));
      setReportDate(normalizeDateValue(report.reportDate));
      setNotes(report.notes ?? '');
      setTotalRevenue(Number(report.totalRevenue ?? 0));
      setSelectedUsers((report.users ?? []).map(normalizeUserSnapshot));
      setAttachments(report.attachments ?? []);
      if (!preservePendingFiles) {
        clearPendingFiles();
      }
    },
    [clearPendingFiles],
  );

  const loadReport = useCallback(
    async (id: number) => {
      try {
        setLoadingReportId(id);
        const response = await axiosInstance.get<IDailyInventoryClose>(ApiConstants.dailyInventoryClose.byId(id));
        hydrateReport(response.data);
      } catch (e: any) {
        console.error(e);
        toast.error(
          i18n.t('dailyInventoryClose.messages.loadError', {
            defaultValue: 'Failed to load report.',
          }),
        );
      } finally {
        setLoadingReportId(null);
      }
    },
    [hydrateReport, i18n],
  );

  const loadUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(ApiConstants.User.users);
      const activeUsers: IUser[] = (response.data ?? []).filter((item: IUser) => !item.isDelete);
      setUsers(activeUsers);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadRecentReports = useCallback(async () => {
    try {
      setLoadingReports(true);
      const response = await axiosInstance.post<IDailyInventoryCloseListResponse>(ApiConstants.dailyInventoryClose.listCt, {
        pagination: { page: 1, itemPerPage: 8 },
      });
      setRecentReports(response.data?.items ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadUsers();
    loadRecentReports();
  }, [loadRecentReports, loadUsers, open]);

  useEffect(() => {
    return () => {
      clearPendingFiles();
    };
  }, [clearPendingFiles]);

  useEffect(() => {
    if (!open) return;

    const id = Number(reportIdProp ?? 0);
    if (id > 0) {
      loadReport(id);
    } else {
      newReport();
    }
  }, [loadReport, open, reportIdProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const addUser = async () => {
    if (!selectedUserId) {
      toast.warning(i18n.t('dailyInventoryClose.messages.selectUser', { defaultValue: 'Select a user first.' }));
      return;
    }

    if (selectedUsers.some((item) => item.userId === selectedUserId)) {
      toast.warning(i18n.t('dailyInventoryClose.messages.duplicateUser', { defaultValue: 'This user already exists in the report.' }));
      return;
    }

    try {
      setLoadingPreview(true);
      setInfoMessage('');
      const response = await axiosInstance.post<IDailyInventoryCloseUser>(ApiConstants.dailyInventoryClose.previewUser, {
        userId: selectedUserId,
        reportDate,
      });

      setSelectedUsers((prev) => {
        const next = [...prev, normalizeUserSnapshot(response.data)];
        setTotalRevenue(next.reduce((sum, item) => sum + Number(item.totalRevenue || 0), 0));
        return next;
      });
      setSelectedUserId(0);
      if (!response.data.products?.length) {
        setInfoMessage(i18n.t('dailyInventoryClose.messages.noProductsForUser', { defaultValue: 'The selected user has no closed invoice rows for this date.' }));
      }
    } catch (e: any) {
      console.error(e);
      toast.error(
        i18n.t('dailyInventoryClose.messages.previewError', {
          defaultValue: 'Failed to preview user data.',
        }),
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const removeUser = (userId: number) => {
    setSelectedUsers((prev) => {
      const next = prev.filter((item) => item.userId !== userId);
      setTotalRevenue(next.reduce((sum, item) => sum + Number(item.totalRevenue || 0), 0));
      return next;
    });
  };

  const calculateRevenue = async () => {
    try {
      setLoadingRevenue(true);
      const response = await axiosInstance.post<any>(ApiConstants.companyBalance.report, {
        dateFrom: reportDate,
        dateTo: reportDate,
        fromDate: reportDate,
        toDate: reportDate,
        startDate: reportDate,
        endDate: reportDate,
        userId: 0,
        invoiceType: 'all',
      });
      const lines = Array.isArray(response.data?.lines)
        ? response.data.lines
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];
      const fallbackTotal = lines.reduce((sum: number, item: any) => sum + Number(item.revenue ?? item.totalRevenue ?? item.totalValue ?? item.value ?? 0), 0);
      setTotalRevenue(Number(response.data?.totalRevenue ?? response.data?.revenue ?? response.data?.totalValue ?? fallbackTotal));
    } catch (e: any) {
      console.error(e);
      toast.error(
        i18n.t('dailyInventoryClose.messages.revenueError', {
          defaultValue: 'Failed to calculate revenue.',
        }),
      );
    } finally {
      setLoadingRevenue(false);
    }
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    const safeFiles = files.filter((file) => {
      const isAccepted = ACCEPTED_IMAGE_TYPES.includes(file.type);
      if (!isAccepted) {
        toast.warning(`${file.name}: ${i18n.t('dailyInventoryClose.messages.invalidFileType', { defaultValue: 'Only jpg, jpeg, png and webp files are allowed.' })}`);
      }
      return isAccepted;
    });

    if (!safeFiles.length) return;

    try {
      const compressed = await compressImagesIfNeeded(safeFiles, {
        maxKB: MAX_ATTACHMENT_KB,
        outputType: 'image/jpeg',
      });
      setPendingFiles((prev) => [
        ...prev,
        ...compressed.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ]);
    } catch (e) {
      console.error(e);
      setPendingFiles((prev) => [
        ...prev,
        ...safeFiles.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ]);
    }
  };

  const uploadPendingFiles = async (savedReportId: number): Promise<boolean> => {
    if (!pendingFiles.length) return true;

    let allSucceeded = true;
    const failedFiles: PendingAttachment[] = [];

    for (const item of pendingFiles) {
      try {
        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('EntityId', String(savedReportId));
        formData.append('EntityType', String(ApiConstants.EntityTypes.DailyInventoryClose));

        await axiosInstance.post(ApiConstants.file.upload, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        URL.revokeObjectURL(item.previewUrl);
      } catch (e) {
        console.error(e);
        allSucceeded = false;
        failedFiles.push(item);
      }
    }

    if (!allSucceeded) {
      toast.warning(
        i18n.t('dailyInventoryClose.messages.someUploadsFailed', {
          defaultValue: 'Some attachments could not be uploaded. Please try again.',
        }),
      );
    }

    setPendingFiles(failedFiles);
    return allSucceeded;
  };

  const saveReport = async () => {
    if (!selectedUsers.length) {
      toast.warning(i18n.t('dailyInventoryClose.messages.addAtLeastOneUser', { defaultValue: 'Add at least one user before saving.' }));
      return;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post<IDailyInventoryClose>(ApiConstants.dailyInventoryClose.save, {
        id: reportId,
        reportDate,
        notes,
        totalRevenue: totalSelectedRevenue,
        users: selectedUsers,
      });

      const savedId = Number(response.data.id);
      if (savedId > 0 && pendingFiles.length) {
        const uploadsSucceeded = await uploadPendingFiles(savedId);
        if (uploadsSucceeded) {
          await loadReport(savedId);
        } else {
          hydrateReport(response.data, true);
        }
      } else {
        hydrateReport(response.data);
      }

      await loadRecentReports();
      toast.success(i18n.t('dailyInventoryClose.messages.saved', { defaultValue: 'Daily inventory close saved.' }));
      onSaved?.();
    } catch (e: any) {
      console.error(e);
      toast.error(
        i18n.t('dailyInventoryClose.messages.saveError', {
          defaultValue: 'Failed to save report.',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const newReport = () => {
    setReportId(0);
    setReportDate(formatDateForInput(new Date()));
    setSelectedUserId(0);
    setSelectedUsers([]);
    setNotes('');
    setTotalRevenue(0);
    setAttachments([]);
    clearPendingFiles();
    setInfoMessage('');
  };

  const deleteAttachment = async (attachmentId: number) => {
    try {
      setDeletingAttachmentId(attachmentId);
      await axiosInstance.delete(ApiConstants.file.deleteById(attachmentId));
      setAttachments((prev) => (prev ?? []).filter((item) => item.id !== attachmentId));
      toast.success(i18n.t('dailyInventoryClose.messages.attachmentDeleted', { defaultValue: 'Attachment removed.' }));
    } catch (e: any) {
      console.error(e);
      toast.error(
        i18n.t('dailyInventoryClose.messages.attachmentDeleteError', {
          defaultValue: 'Failed to delete attachment.',
        }),
      );
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const item = prev[index];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleReportDateChange = (nextDate: string) => {
    setReportDate(nextDate);
    setInfoMessage('');

    if (selectedUsers.length) {
      setSelectedUsers([]);
      setSelectedUserId(0);
      toast.info(
        i18n.t('dailyInventoryClose.messages.usersClearedAfterDateChange', {
          defaultValue: 'Selected users were cleared because report snapshots are tied to the report date.',
        }),
      );
    }
  };

  const buildPdfFileName = () => `daily-inventory-close-${reportDate || formatDateForInput(new Date())}${reportId ? `-${reportId}` : ''}.pdf`;

  const loadPdfAttachments = async () => {
    const savedAttachments = await Promise.all(
      (attachments ?? []).map(async (attachment) => {
        try {
          const response = await axiosInstance.get(ApiConstants.file.downloadByIdUrl(attachment.id), {
            responseType: 'blob',
          });
          return {
            id: attachment.id,
            fileName: attachment.fileName || `Attachment #${attachment.id}`,
            dataUrl: await blobToDataUrl(response.data),
          };
        } catch (e) {
          console.error(e);
          return {
            id: attachment.id,
            fileName: attachment.fileName || `Attachment #${attachment.id}`,
            dataUrl: '',
          };
        }
      }),
    );

    const localAttachments = await Promise.all(
      pendingFiles.map(async (item, index) => ({
        id: `pending-${index}`,
        fileName: item.file.name,
        dataUrl: await blobToDataUrl(item.file),
      })),
    );

    return [...savedAttachments, ...localAttachments];
  };

  const buildReportPdf = async () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 38;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addPageIfNeeded = (height = 24) => {
      if (y + height <= pageHeight - margin) return;
      doc.addPage();
      y = margin;
    };

    const addText = (text: string, x: number, options?: any) => {
      const lines = doc.splitTextToSize(text || '', options?.maxWidth ?? contentWidth - (x - margin));
      addPageIfNeeded(lines.length * (options?.lineHeight ?? 14));
      doc.text(lines, x, y, options);
      y += lines.length * (options?.lineHeight ?? 14);
    };

    const addRule = () => {
      addPageIfNeeded(12);
      doc.setDrawColor(210);
      doc.line(margin, y, pageWidth - margin, y);
      y += 14;
    };

    const addSectionTitle = (title: string) => {
      addPageIfNeeded(30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(20, 30, 45);
      doc.text(title, margin, y);
      y += 18;
    };

    const drawCell = (text: string, x: number, width: number, rowY: number, align: 'left' | 'right' = 'left') => {
      const displayText = String(text ?? '');
      doc.text(displayText, align === 'right' ? x + width : x, rowY, {
        align,
        maxWidth: width - 8,
      } as any);
    };

    const addAttachmentImage = (dataUrl: string, fileName: string) => {
      if (!dataUrl) {
        addText(`${fileName} - ${i18n.t('dailyInventoryClose.messages.attachmentLoadError', { defaultValue: 'Could not load image.' })}`, margin);
        return;
      }

      try {
        const properties = doc.getImageProperties(dataUrl);
        const labelHeight = 16;
        const maxImageHeight = 260;
        const imageRatio = properties.width / properties.height || 1;
        let imageWidth = contentWidth;
        let imageHeight = imageWidth / imageRatio;

        if (imageHeight > maxImageHeight) {
          imageHeight = maxImageHeight;
          imageWidth = imageHeight * imageRatio;
        }

        addPageIfNeeded(labelHeight + imageHeight + 24);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(fileName || '-', margin, y);
        y += labelHeight;
        doc.addImage(dataUrl, properties.fileType || 'JPEG', margin, y, imageWidth, imageHeight, undefined, 'FAST');
        y += imageHeight + 18;
      } catch (e) {
        console.error(e);
        addText(`${fileName} - ${i18n.t('dailyInventoryClose.messages.attachmentLoadError', { defaultValue: 'Could not load image.' })}`, margin);
      }
    };

    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, pageWidth, 86, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(i18n.t('dailyInventoryClose.pdf.title', { defaultValue: 'Daily Inventory Close Report' }), margin, 38);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${i18n.t('dailyInventoryClose.fields.reportDate', { defaultValue: 'Report Date' })}: ${reportDate}`, margin, 60);
    doc.text(`${i18n.t('dailyInventoryClose.stats.reportId', { defaultValue: 'Report' })}: ${reportId ? `#${reportId}` : i18n.t('generic.new', { defaultValue: 'New' })}`, pageWidth - margin, 60, { align: 'right' });
    y = 112;

    const summary = [
      [i18n.t('dailyInventoryClose.stats.selectedUsers', { defaultValue: 'Selected Users' }), String(selectedUsers.length)],
      [i18n.t('dailyInventoryClose.stats.selectedRevenue', { defaultValue: 'Selected Revenue' }), formatCurrency(totalSelectedRevenue)],
      [i18n.t('dailyInventoryClose.stats.invoiceRevenue', { defaultValue: 'Invoice Revenue' }), formatCurrency(totalRevenue)],
      [i18n.t('dailyInventoryClose.sections.attachments', { defaultValue: 'Attachments' }), String((attachments?.length ?? 0) + pendingFiles.length)],
    ];

    doc.setTextColor(15, 23, 42);
    summary.forEach(([label, value], index) => {
      const colWidth = contentWidth / 2;
      const x = margin + (index % 2) * colWidth;
      const boxY = y + Math.floor(index / 2) * 58;
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x, boxY, colWidth - 8, 46, 5, 5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(label.toUpperCase(), x + 10, boxY + 15);
      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text(value, x + 10, boxY + 34);
    });
    y += 122;

    addSectionTitle(i18n.t('dailyInventoryClose.sections.userSummaries', { defaultValue: 'User summaries' }));

    selectedUsers.forEach((user) => {
      addPageIfNeeded(84);
      doc.setFillColor(241, 245, 249);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(margin, y, contentWidth, 34, 5, 5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(user.userDisplayName || `User #${user.userId}`, margin + 10, y + 21);
      doc.text(formatCurrency(user.totalRevenue), pageWidth - margin - 10, y + 21, { align: 'right' });
      y += 44;

      const productW = contentWidth - 170;
      const qtyW = 70;
      const revenueW = 90;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      drawCell(i18n.t('dailyInventoryClose.table.product', { defaultValue: 'Product' }), margin, productW, y);
      drawCell(i18n.t('dailyInventoryClose.table.qty', { defaultValue: 'Qty' }), margin + productW, qtyW, y, 'right');
      drawCell(i18n.t('dailyInventoryClose.table.revenue', { defaultValue: 'Revenue' }), margin + productW + qtyW, revenueW, y, 'right');
      y += 10;
      addRule();

      (user.products?.length ? user.products : []).forEach((product) => {
        const productLabel = `${product.productName || ''}${product.sourceType ? ` (${product.sourceType})` : ''}`;
        const productLines = doc.splitTextToSize(productLabel, productW - 8);
        const rowHeight = Math.max(18, productLines.length * 11 + 6);
        addPageIfNeeded(rowHeight);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(15, 23, 42);
        doc.text(productLines, margin, y);
        drawCell(formatNumber(product.quantity, i18n.language), margin + productW, qtyW, y, 'right');
        drawCell(formatCurrency(product.revenue), margin + productW + qtyW, revenueW, y, 'right');
        y += rowHeight;
      });

      if (!user.products?.length) {
        addText(i18n.t('dailyInventoryClose.empty.noProducts', { defaultValue: 'No grouped products.' }), margin);
      }

      y += 10;
    });

    const plainNotes = stripHtml(notes);
    addSectionTitle(i18n.t('dailyInventoryClose.sections.notes', { defaultValue: 'Manager notes' }));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    addText(plainNotes || '-', margin, { lineHeight: 14 });

    const pdfAttachments = await loadPdfAttachments();
    addSectionTitle(i18n.t('dailyInventoryClose.sections.attachments', { defaultValue: 'Attachments' }));
    if (pdfAttachments.length) {
      pdfAttachments.forEach((attachment) => {
        addAttachmentImage(attachment.dataUrl, attachment.fileName);
      });
    } else {
      addText(i18n.t('dailyInventoryClose.empty.noAttachments', { defaultValue: 'No photos attached yet.' }), margin);
    }

    const footer = i18n.t('dailyInventoryClose.pdf.generatedAt', {
      defaultValue: 'Generated at {{date}}',
      date: new Date().toLocaleString(),
    });
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(footer, margin, pageHeight - 20);
      doc.text(`${page}/${totalPages}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
    }

    return doc;
  };

  const downloadReportPdf = async () => {
    if (!selectedUsers.length) {
      toast.warning(i18n.t('dailyInventoryClose.messages.addAtLeastOneUser', { defaultValue: 'Add at least one user before saving.' }));
      return;
    }

    const doc = await buildReportPdf();
    doc.save(buildPdfFileName());
  };

  const selectedSendUser = useMemo(() => users.find((user) => user.id === sendUserId), [sendUserId, users]);

  const sendReportEmail = async () => {
    if (!selectedUsers.length) {
      toast.warning(i18n.t('dailyInventoryClose.messages.addAtLeastOneUser', { defaultValue: 'Add at least one user before saving.' }));
      return;
    }

    if (!selectedSendUser?.email) {
      toast.warning(i18n.t('dailyInventoryClose.messages.selectEmailUser', { defaultValue: 'Select a user with an email address.' }));
      return;
    }

    try {
      setSendingReport(true);
      const doc = await buildReportPdf();
      const base64 = String(doc.output('datauristring')).split(',')[1];
      const recipientName = displayUserName(selectedSendUser);
      const subject = i18n.t('dailyInventoryClose.email.subject', {
        defaultValue: 'Daily Inventory Close - {{date}}',
        date: reportDate,
      });

      await axiosInstance.post(ApiConstants.email.send, {
        recipients: [selectedSendUser.email],
        subject,
        isHtml: true,
        template: `
          <div style="font-family:Arial,sans-serif;color:#0f172a;line-height:1.5">
            <h2 style="margin:0 0 12px">${subject}</h2>
            <p>${i18n.t('dailyInventoryClose.email.greeting', { defaultValue: 'Hello {{name}},', name: recipientName })}</p>
            <p>${i18n.t('dailyInventoryClose.email.body', { defaultValue: 'The daily inventory close report is attached as a PDF.' })}</p>
            <ul>
              <li>${i18n.t('dailyInventoryClose.stats.selectedUsers', { defaultValue: 'Selected Users' })}: ${selectedUsers.length}</li>
              <li>${i18n.t('dailyInventoryClose.stats.selectedRevenue', { defaultValue: 'Selected Revenue' })}: ${formatCurrency(totalSelectedRevenue)}</li>
              <li>${i18n.t('dailyInventoryClose.stats.invoiceRevenue', { defaultValue: 'Invoice Revenue' })}: ${formatCurrency(totalRevenue)}</li>
            </ul>
          </div>
        `,
        attachments: [
          {
            fileName: buildPdfFileName(),
            contentType: 'application/pdf',
            base64Content: base64,
          },
        ],
      });

      toast.success(i18n.t('dailyInventoryClose.messages.emailQueued', { defaultValue: 'Report email queued.' }));
      setSendDialogOpen(false);
      setSendUserId(0);
    } catch (e) {
      console.error(e);
      toast.error(i18n.t('dailyInventoryClose.messages.emailError', { defaultValue: 'Failed to send report email.' }));
    } finally {
      setSendingReport(false);
    }
  };

  const totalSelectedRevenue = selectedUsers.reduce((sum, item) => sum + Number(item.totalRevenue || 0), 0);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen
      PaperProps={{ className: `dicDialogPaper bcTheme-${theme}` }}
    >
      <DialogContent className="dicDialogContent">
        <div className="dicPage">
        <header className="dicHeader dicPanel">
          <div>
            <Typography className="dicTitle" variant="h4">
              {i18n.t('dailyInventoryClose.title', { defaultValue: 'Daily Inventory Close' })}
            </Typography>
            <Typography className="dicSubtitle">
              {i18n.t('dailyInventoryClose.subtitle', { defaultValue: 'End-of-day inventory reconciliation with invoice snapshots, notes and photos.' })}
            </Typography>
          </div>
          <div className="dicHeaderActions">
            <Button className="dicBtn" variant="outlined" startIcon={<Close />} onClick={onClose} disabled={saving}>
              {i18n.t('dailyInventoryClose.actions.close', { defaultValue: 'Close' })}
            </Button>
            <Button className="dicBtn" variant="outlined" startIcon={<Refresh />} onClick={newReport}>
              {i18n.t('dailyInventoryClose.actions.new', { defaultValue: 'New' })}
            </Button>
            <Button className="dicBtn" variant="outlined" startIcon={<PictureAsPdf />} onClick={downloadReportPdf}>
              {i18n.t('dailyInventoryClose.actions.pdf', { defaultValue: 'PDF' })}
            </Button>
            <Button className="dicBtn" variant="outlined" startIcon={<SendOutlined />} onClick={() => setSendDialogOpen(true)}>
              {i18n.t('dailyInventoryClose.actions.send', { defaultValue: 'Send' })}
            </Button>
            <Button className="dicBtn" variant="contained" startIcon={<Save />} onClick={saveReport} disabled={saving}>
              {saving ? i18n.t('generic.saving', { defaultValue: 'Saving...' }) : i18n.t('actions.save', { defaultValue: 'Save' })}
            </Button>
          </div>
        </header>

        {infoMessage && (
          <div className="dicMessage">
            <Alert severity="info">{infoMessage}</Alert>
          </div>
        )}

        <section className="dicFilters dicPanel">
          <div className="dicFiltersGrid">
            <FormControl fullWidth size="small">
              <InputLabel>{i18n.t('dailyInventoryClose.fields.user', { defaultValue: 'User' })}</InputLabel>
              <Select
                value={selectedUserId}
                label={i18n.t('dailyInventoryClose.fields.user', { defaultValue: 'User' })}
                onChange={(event) => setSelectedUserId(Number(event.target.value))}
              >
                <MenuItem value={0}>{i18n.t('dailyInventoryClose.fields.selectUser', { defaultValue: 'Select user' })}</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {displayUserName(user)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button className="dicBtn" variant="contained" startIcon={<Add />} onClick={addUser} disabled={loadingPreview || !selectedUser}>
              {loadingPreview ? i18n.t('generic.loading', { defaultValue: 'Loading...' }) : i18n.t('dailyInventoryClose.actions.addUser', { defaultValue: 'Add User' })}
            </Button>

            <TextField
              fullWidth
              size="small"
              type="date"
              label={i18n.t('dailyInventoryClose.fields.reportDate', { defaultValue: 'Report Date' })}
              value={reportDate}
              onChange={(event) => handleReportDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </section>

        <section className="dicStats">
          <div className="dicStat dicPanel">
            <span>{i18n.t('dailyInventoryClose.stats.reportId', { defaultValue: 'Report' })}</span>
            <strong>{reportId ? `#${reportId}` : i18n.t('generic.new', { defaultValue: 'New' })}</strong>
          </div>
          <div className="dicStat dicPanel">
            <span>{i18n.t('dailyInventoryClose.stats.selectedUsers', { defaultValue: 'Selected Users' })}</span>
            <strong>{selectedUsers.length}</strong>
          </div>
          <div className="dicStat dicPanel isRevenue">
            <span>{i18n.t('dailyInventoryClose.stats.selectedRevenue', { defaultValue: 'Selected Revenue' })}</span>
            <strong>{formatCurrency(totalSelectedRevenue)}</strong>
          </div>
          <div className="dicStat dicPanel">
            <span>{i18n.t('dailyInventoryClose.stats.invoiceRevenue', { defaultValue: 'Invoice Revenue' })}</span>
            <strong>{formatCurrency(totalRevenue)}</strong>
          </div>
        </section>

        <div className="dicLayout">
          <main className="dicMain">
            <section className="dicPanel dicUsersPanel">
              <div className="dicPanelHeader">
                <div>
                  <h3>{i18n.t('dailyInventoryClose.sections.userSummaries', { defaultValue: 'User summaries' })}</h3>
                  <p>{i18n.t('dailyInventoryClose.sections.userSummariesHint', { defaultValue: 'Each user is saved as a historical product snapshot.' })}</p>
                </div>
              </div>

              {!selectedUsers.length ? (
                <div className="dicEmpty">
                  <Inventory2 />
                  <strong>{i18n.t('dailyInventoryClose.empty.noUsers', { defaultValue: 'No users added yet' })}</strong>
                </div>
              ) : (
                <div className="dicUserCards">
                  {selectedUsers.map((user) => (
                    <article className="dicUserCard" key={user.userId}>
                      <div className="dicUserCardTop">
                        <div>
                          <div className="dicUserName">{user.userDisplayName}</div>
                          <div className="dicUserRevenue">{formatCurrency(user.totalRevenue)}</div>
                        </div>
                        <Tooltip title={i18n.t('generic.remove', { defaultValue: 'Remove' })}>
                          <IconButton className="dicIconDanger" onClick={() => removeUser(user.userId)}>
                            <RemoveCircleOutline />
                          </IconButton>
                        </Tooltip>
                      </div>

                      <TableContainer component={Paper} className="dicProductTableWrap">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{i18n.t('dailyInventoryClose.table.product', { defaultValue: 'Product' })}</TableCell>
                              <TableCell align="right">{i18n.t('dailyInventoryClose.table.qty', { defaultValue: 'Qty' })}</TableCell>
                              <TableCell align="right">{i18n.t('dailyInventoryClose.table.revenue', { defaultValue: 'Revenue' })}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {user.products?.length ? (
                              user.products.map((product, index) => (
                                <TableRow key={`${product.sourceType}-${product.productId ?? product.productName}-${index}`}>
                                  <TableCell>
                                    <div className="dicProductName">{product.productName}</div>
                                    <div className="dicProductSource">{product.sourceType}</div>
                                  </TableCell>
                                  <TableCell align="right">{formatNumber(product.quantity, i18n.language)}</TableCell>
                                  <TableCell align="right" className="dicMoney">
                                    {formatCurrency(product.revenue)}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  {i18n.t('dailyInventoryClose.empty.noProducts', { defaultValue: 'No grouped products.' })}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="dicPanel dicEditorPanel">
              <div className="dicPanelHeader">
                <div>
                  <h3>{i18n.t('dailyInventoryClose.sections.notes', { defaultValue: 'Manager notes' })}</h3>
                  <p>{i18n.t('dailyInventoryClose.sections.notesHint', { defaultValue: 'Missing inventory, staff notes, discrepancies or damage reports.' })}</p>
                </div>
              </div>
              <ReactQuill theme="snow" value={notes} onChange={setNotes} className="dicEditor" />
            </section>

            <section className="dicPanel dicRevenuePanel">
              <div className="dicPanelHeader">
                <div>
                  <h3>{i18n.t('dailyInventoryClose.sections.revenueValidation', { defaultValue: 'Revenue validation' })}</h3>
                  <p>{i18n.t('dailyInventoryClose.sections.revenueValidationHint', { defaultValue: 'Uses api/company-balance/report for the selected day.' })}</p>
                </div>
              </div>
              <div className="dicRevenueControls">
                <TextField
                  fullWidth
                  size="small"
                  label={i18n.t('dailyInventoryClose.fields.totalInvoicesAmount', { defaultValue: 'Total Invoices Amount' })}
                  value={formatCurrency(totalRevenue)}
                  InputProps={{ readOnly: true }}
                />
                <Button className="dicBtn" variant="contained" startIcon={<Calculate />} onClick={calculateRevenue} disabled={loadingRevenue}>
                  {loadingRevenue ? i18n.t('generic.calculating', { defaultValue: 'Calculating...' }) : i18n.t('dailyInventoryClose.actions.calculateRevenue', { defaultValue: 'Calculate Revenue' })}
                </Button>
              </div>
            </section>
          </main>

          <aside className="dicSide">
            <section className="dicPanel dicAttachmentsPanel">
              <div className="dicPanelHeader">
                <div>
                  <h3>{i18n.t('dailyInventoryClose.sections.attachments', { defaultValue: 'Attachments' })}</h3>
                  <p>{i18n.t('dailyInventoryClose.sections.attachmentsHint', { defaultValue: 'Inventory, cash drawer, damage or end-of-day photos.' })}</p>
                </div>
              </div>

              <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleFileSelection} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={handleFileSelection} />
              <div className="dicAttachmentActions">
                <Button className="dicUploadBtn" variant="outlined" startIcon={<AddPhotoAlternate />} onClick={() => fileInputRef.current?.click()}>
                  {i18n.t('dailyInventoryClose.actions.choosePhotos', { defaultValue: 'Choose photos' })}
                </Button>
                <Button className="dicUploadBtn" variant="outlined" startIcon={<PhotoCamera />} onClick={() => cameraInputRef.current?.click()}>
                  {i18n.t('dailyInventoryClose.actions.takePhoto', { defaultValue: 'Take photo' })}
                </Button>
              </div>

              <div className="dicAttachmentGrid">
                {(attachments ?? []).map((attachment) => (
                  <div className="dicAttachment" key={attachment.id}>
                    <img src={getMetadataFileUrl(attachment.id)} alt={attachment.fileName ?? 'Attachment'} />
                    <button
                      type="button"
                      onClick={() => deleteAttachment(attachment.id)}
                      disabled={deletingAttachmentId === attachment.id}
                      title={i18n.t('generic.delete', { defaultValue: 'Delete' })}
                    >
                      <DeleteOutline fontSize="small" />
                    </button>
                  </div>
                ))}

                {pendingFiles.map((item, index) => (
                  <div className="dicAttachment isPending" key={`${item.file.name}-${index}`}>
                    <img src={item.previewUrl} alt={item.file.name} />
                    <button type="button" onClick={() => removePendingFile(index)} title={i18n.t('generic.remove', { defaultValue: 'Remove' })}>
                      <DeleteOutline fontSize="small" />
                    </button>
                    <span>{i18n.t('dailyInventoryClose.labels.pending', { defaultValue: 'Pending' })}</span>
                  </div>
                ))}
              </div>

              {!attachments?.length && !pendingFiles.length && (
                <div className="dicAttachmentEmpty">{i18n.t('dailyInventoryClose.empty.noAttachments', { defaultValue: 'No photos attached yet.' })}</div>
              )}
            </section>

            <section className="dicPanel dicReportsPanel">
              <div className="dicPanelHeader">
                <div>
                  <h3>{i18n.t('dailyInventoryClose.sections.recentReports', { defaultValue: 'Recent reports' })}</h3>
                  <p>{i18n.t('dailyInventoryClose.sections.recentReportsHint', { defaultValue: 'Open a saved close report for editing.' })}</p>
                </div>
              </div>

              {loadingReports ? (
                <div className="dicMiniLoader">
                  <CircularProgress size={22} />
                </div>
              ) : !recentReports.length ? (
                <div className="dicAttachmentEmpty">{i18n.t('dailyInventoryClose.empty.noReports', { defaultValue: 'No saved reports yet.' })}</div>
              ) : (
                <div className="dicReportList">
                  {recentReports.map((report) => (
                    <button type="button" className={`dicReportItem ${report.id === reportId ? 'isActive' : ''}`} key={report.id} onClick={() => loadReport(report.id)}>
                      <span className="dicReportDate">
                        <EventNote fontSize="small" />
                        {normalizeDateValue(report.reportDate)}
                      </span>
                      <strong>{formatCurrency(report.totalRevenue)}</strong>
                      <span>{i18n.t('dailyInventoryClose.labels.usersCount', { defaultValue: '{{count}} users', count: report.userCount })}</span>
                      {loadingReportId === report.id ? <CircularProgress size={16} /> : <Edit fontSize="small" />}
                    </button>
                  ))}
                </div>
              )}
            </section>
          </aside>
        </div>
      </div>
      <Dialog
        open={sendDialogOpen}
        onClose={() => !sendingReport && setSendDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: `dicSendDialogPaper bcTheme-${theme}` }}
      >
        <DialogTitle className="dicSendDialogTitle">
          {i18n.t('dailyInventoryClose.sendDialog.title', { defaultValue: 'Send report' })}
        </DialogTitle>
        <DialogContent className="dicSendDialogBody">
          <FormControl fullWidth size="small">
            <InputLabel>{i18n.t('dailyInventoryClose.sendDialog.user', { defaultValue: 'User' })}</InputLabel>
            <Select
              value={sendUserId}
              label={i18n.t('dailyInventoryClose.sendDialog.user', { defaultValue: 'User' })}
              onChange={(event) => setSendUserId(Number(event.target.value))}
            >
              <MenuItem value={0}>{i18n.t('dailyInventoryClose.sendDialog.selectUser', { defaultValue: 'Select user' })}</MenuItem>
              {users
                .filter((user) => !user.isDelete && !!user.email)
                .map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {displayUserName(user)} - {user.email}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className="dicSendDialogActions">
          <Button onClick={() => setSendDialogOpen(false)} disabled={sendingReport}>
            {i18n.t('actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" startIcon={<SendOutlined />} onClick={sendReportEmail} disabled={sendingReport || !sendUserId}>
            {sendingReport
              ? i18n.t('dailyInventoryClose.actions.sending', { defaultValue: 'Sending...' })
              : i18n.t('dailyInventoryClose.actions.send', { defaultValue: 'Send' })}
          </Button>
        </DialogActions>
      </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default DailyInventoryClose;
